import { ExamCenterProctorType } from '@exam-center/graphql';
import {
  PROCTOR_TYPE_TEXTS,
  useGetProctorStorageObjectFiles,
  type RequiredProctorStorageObjectFields,
} from '@exam-center/proctor-management';
import {
  ProctorDocumentFields,
  ProctorEmployeeInformationFields,
  ProctorPersonalInformationFields,
  ProctorTypeField,
} from '@exam-center/proctor-request-management';
import {
  Button,
  ConfirmationModal,
  Divider,
  Flex,
  Form,
  Loading,
  PaperSection,
  Text,
  type FormProps,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { formatDate } from '@shared/utils';

import {
  type ExamCenterProctorDetailsFormInput,
  useExamCenterProctorDetailsFormContext,
} from '../../providers';

interface InputFieldProps {
  name: string;
}

export interface ExamCenterProctorDetailsEditFormProps extends Omit<FormProps, 'onSubmit'> {
  data: {
    id: string;
    type: ExamCenterProctorType;
    idNumber: string;
    nameTh: {
      title: string;
      firstName: string;
      lastName: string;
    };
    nameEn: {
      title: string;
      firstName: string;
      lastName: string;
    };
    email?: string;
    startedAt: Date;
    endedAt: Date | null;
    agreementFileUrls: RequiredProctorStorageObjectFields[];
  };
  fields: {
    personal: {
      email: InputFieldProps;
      phoneNumber: InputFieldProps;
      address: InputFieldProps;
    };
    employeeInfo: {
      employeeId: InputFieldProps;
      position: InputFieldProps;
      department: InputFieldProps;
    };
  };
  onSubmit: (data: ExamCenterProctorDetailsFormInput) => void;
  isSubmitting?: boolean;
}

export default function ExamCenterProctorDetailsEditForm(
  props: Readonly<ExamCenterProctorDetailsEditFormProps>,
) {
  const { data, fields, isSubmitting, onSubmit, ...restProps } = props;
  const [isConfirmationModalOpen, confirmationModalHandler] = useDisclosure();

  const formContext = useExamCenterProctorDetailsFormContext();

  const { result: agreementFiles, isLoading } = useGetProctorStorageObjectFiles({
    storageObjectKeys: data.agreementFileUrls,
    proctorId: data.id,
  });

  const handleClickSaveButton = () => {
    formContext.trigger().then((isValid) => {
      if (isValid) {
        confirmationModalHandler.open();
      }
    });
  };

  const handleSave = () => {
    formContext
      .handleSubmit(onSubmit)()
      .then(() => {
        confirmationModalHandler.close();
      });
  };

  return (
    <Form {...restProps}>
      <PaperSection title='ข้อมูลผู้คุมสอบ'>
        <Flex
          sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6), p: theme.spacing(6) })}
        >
          <ProctorTypeField
            fields={{
              proctorType: {
                mode: 'view',
                value: <Text>{PROCTOR_TYPE_TEXTS[data.type]}</Text>,
              },
            }}
          />
          <Divider />
          <ProctorPersonalInformationFields
            fields={{
              idNumber: { mode: 'view', value: data.idNumber },
              titleTh: {
                mode: 'view',
                value: data.nameTh.title,
              },
              firstNameTh: { mode: 'view', value: data.nameTh.firstName },
              lastNameTh: { mode: 'view', value: data.nameTh.lastName },
              titleEn: {
                mode: 'view',
                value: data.nameEn.title,
              },
              firstNameEn: { mode: 'view', value: data.nameEn.firstName },
              lastNameEn: { mode: 'view', value: data.nameEn.lastName },
              email:
                data.type === ExamCenterProctorType.Proctor_3
                  ? { mode: 'input', name: fields.personal.email.name }
                  : {
                      mode: 'view',
                      value: data.email ?? '-',
                    },
              phoneNumber: { mode: 'input', name: fields.personal.phoneNumber.name },
              address: { mode: 'input', name: fields.personal.address.name },
            }}
          />
          <Divider />
          <ProctorEmployeeInformationFields
            fields={{
              startedAt: {
                mode: 'view',
                value: formatDate(data.startedAt),
              },
              endedAt: {
                mode: 'view',
                value: formatDate(data.endedAt),
              },
              code: {
                mode: 'input',
                name: fields.employeeInfo.employeeId.name,
              },
              position: {
                mode: 'input',
                name: fields.employeeInfo.position.name,
              },
              department: {
                mode: 'input',
                name: fields.employeeInfo.department.name,
              },
              isTerminationAssign: {
                mode: 'view',
                value: <Text>ไม่มีกำหนด</Text>,
              },
            }}
            options={{
              shouldShowEndedAtDateDisplay: !!data.endedAt,
            }}
          />
          <Divider />
          {isLoading ? (
            <Loading sx={{ height: '120px' }} />
          ) : (
            <ProctorDocumentFields
              hideIdentificationFiles
              fields={{
                agreementForms: {
                  mode: 'view',
                  slotProps: {
                    files: agreementFiles,
                  },
                },
              }}
            />
          )}
        </Flex>
      </PaperSection>
      <Flex sx={(theme) => ({ flex: 1, justifyContent: 'flex-end', mt: theme.spacing(6) })}>
        <Button
          size='large'
          onClick={handleClickSaveButton}
          disabled={!formContext.formState.isDirty}
        >
          บันทึก
        </Button>
      </Flex>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        title='ยืนยันการแก้ไขข้อมูล?'
        content='คุณกำลังจะแก้ไขข้อมูลผู้คุมสอบ กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ'
        acceptText='ยืนยัน'
        discardText='ยกเลิก'
        onClose={confirmationModalHandler.close}
        onDiscard={confirmationModalHandler.close}
        onAccept={handleSave}
        loading={isSubmitting}
      />
    </Form>
  );
}
